/**
 * tRPC Client for Consumer Portal
 *
 * Type-safe API client using tRPC with React Query integration.
 * Uses consumer-specific authentication tokens.
 *
 * @module lib/trpc/client
 */

import { httpBatchLink, loggerLink, type TRPCLink } from '@trpc/client';
import { createTRPCReact } from '@trpc/react-query';
import { observable } from '@trpc/server/observable';
import type { AppRouter } from '2-api-gateway/src/trpc/trpc.router';
import { env } from '../../config/env';
import { clearTokens, getAccessToken } from '../auth/storage';

const isDev = import.meta.env.DEV;

/**
 * Trace headers getter for OpenTelemetry integration
 * Set via setTraceHeadersGetter() from TracingProvider
 */
let getTraceHeaders: (() => Record<string, string>) | null = null;

/**
 * Set the trace headers getter function.
 * Called by TRPCProviderInternal after TracingProvider initializes.
 */
export function setTraceHeadersGetter(getter: () => Record<string, string>): void {
  getTraceHeaders = getter;
}

/**
 * Create tRPC React client with type inference from the API router
 */
export const trpc = createTRPCReact<AppRouter>();

/**
 * Get the API URL from centralized environment configuration
 */
function getBaseUrl(): string {
  return env.VITE_API_URL;
}

/**
 * Custom error link to handle UNAUTHORIZED errors
 * Redirects to Auth Portal login page when the user's session expires
 */
const errorLink: TRPCLink<AppRouter> = () => {
  return ({ next, op }) => {
    return observable((observer) => {
      const unsubscribe = next(op).subscribe({
        next(value) {
          observer.next(value);
        },
        error(err) {
          // Check if this is an UNAUTHORIZED error
          if (err.data?.code === 'UNAUTHORIZED' || err.data?.httpStatus === 401) {
            console.warn('[tRPC] Unauthorized - redirecting to login');

            // Clear stored tokens
            clearTokens();

            // Redirect to Auth Portal with return URL
            const returnUrl = encodeURIComponent(window.location.href);
            const loginUrl = `${env.VITE_AUTH_PORTAL_URL}/login?returnUrl=${returnUrl}`;
            window.location.href = loginUrl;
          }

          observer.error(err);
        },
        complete() {
          observer.complete();
        },
      });
      return unsubscribe;
    });
  };
};

/**
 * Create tRPC client configuration with consumer authentication
 */
export function createTRPCClient() {
  return trpc.createClient({
    links: [
      loggerLink({
        enabled: (opts) => {
          if (opts.direction === 'down' && opts.result instanceof Error) {
            // Suppress NOT_FOUND errors — expected in dual-fetch patterns
            // (e.g., CaseDetailPage tries both case ID and submission ID lookups)
            if ((opts.result as any).data?.code === 'NOT_FOUND') return false;
            return true;
          }
          return isDev;
        },
      }),
      errorLink,
      httpBatchLink({
        url: `${getBaseUrl()}/trpc`,
        headers() {
          const token = getAccessToken();
          const traceHeaders = getTraceHeaders?.() ?? {};
          return {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...traceHeaders,
          };
        },
      }),
    ],
  });
}

export type { AppRouter };
